import { useCallback, useRef, useState } from 'react'
import { TelemetryEvent, DigId } from '../lib/types'

interface UseDigTelemetryOptions {
  digId: DigId | null
  onBatch?: (events: TelemetryEvent[]) => void
}

export function useDigTelemetry({ digId, onBatch }: UseDigTelemetryOptions) {
  const [events, setEvents] = useState<TelemetryEvent[]>([])
  const startRef = useRef<number>(Date.now())
  const batchRef = useRef<TelemetryEvent[]>([])
  const timerRef = useRef<number | null>(null)

  const now = () => Date.now() - startRef.current

  const record = useCallback((type: string, payload: Record<string, unknown> = {}) => {
    if (!digId) return
    const t = now()
    const evt: TelemetryEvent = {
      digId,
      t,
      type,
      payload: { ...payload, clientTs: Date.now() },
    }
    batchRef.current.push(evt)
    setEvents(prev => [...prev, evt])

    // debounce send
    if (timerRef.current) window.clearTimeout(timerRef.current)
    timerRef.current = window.setTimeout(() => {
      const batch = [...batchRef.current]
      batchRef.current = []
      if (batch.length > 0 && onBatch) onBatch(batch)
    }, 1400)
  }, [digId, onBatch])

  const recordWithLatency = useCallback((type: string, payload: Record<string, unknown>, start: number) => {
    const latency = Date.now() - start
    record(type, { ...payload, latencyMs: latency })
  }, [record])

  const flush = useCallback(() => {
    if (batchRef.current.length > 0 && onBatch && digId) {
      const batch = [...batchRef.current]
      batchRef.current = []
      onBatch(batch)
    }
  }, [digId, onBatch])

  const getPath = useCallback(() => {
    return events.map(e => e.type)
  }, [events])

  const getSkipCount = useCallback(() => {
    return events.filter(e => e.type === 'skip_artifact' || e.type === 'leave_in_situ').length
  }, [events])

  return {
    events,
    record,
    recordWithLatency,
    flush,
    getPath,
    getSkipCount,
    elapsedMs: () => now(),
  }
}
